import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Subscription, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
  ChatApiMessage,
  ChatConversation,
  ChatService,
  ChatStatus,
} from '../../../../services/chat';

@Component({
  selector: 'app-conversation',
  templateUrl: './conversation.component.html',
  styleUrl: './conversation.component.scss',
})
export class ConversationComponent implements OnInit, OnDestroy {
  @ViewChild('messagesBox') messagesBox?: ElementRef<HTMLDivElement>;

  conversationId!: number;
  conversation: ChatConversation | null = null;
  messages: ChatApiMessage[] = [];

  isLoading = false;
  isSending = false;
  isUpdating = false;
  errorMessage = '';
  replyMessage = '';

  private realtimeSub?: Subscription;
  private readonly realtimeIntervalMs = 2000;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private chatService: ChatService
  ) {}

  ngOnInit(): void {
    this.conversationId = Number(this.route.snapshot.paramMap.get('id'));

    if (!this.conversationId) {
      this.router.navigate(['/allchats']);
      return;
    }

    this.loadConversation(true);
    this.startRealtimeMessages();
  }

  ngOnDestroy(): void {
    this.stopRealtimeMessages();
  }

  loadConversation(showLoader = true): void {
    if (showLoader) {
      this.isLoading = true;
    }

    this.errorMessage = '';

    this.chatService.getConversation(this.conversationId).subscribe({
      next: (res) => {
        this.applyConversationResponse(res, true);
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'حصل خطأ أثناء تحميل المحادثة';
        this.isLoading = false;
      },
    });
  }

  startRealtimeMessages(): void {
    this.stopRealtimeMessages();

    this.realtimeSub = timer(this.realtimeIntervalMs, this.realtimeIntervalMs)
      .pipe(
        switchMap(() =>
          this.chatService.getConversation(this.conversationId).pipe(
            catchError(() => EMPTY)
          )
        )
      )
      .subscribe((res) => {
        this.applyConversationResponse(res, false);
      });
  }

  stopRealtimeMessages(): void {
    this.realtimeSub?.unsubscribe();
    this.realtimeSub = undefined;
  }

  sendAdminReply(): void {
    const message = this.replyMessage.trim();

    if (!message || this.isSending) {
      return;
    }

    this.isSending = true;
    this.errorMessage = '';

    this.chatService.adminReply(this.conversationId, message).subscribe({
      next: (res) => {
        this.applyConversationResponse(res, true);
        this.replyMessage = '';
        this.isSending = false;
      },
      error: () => {
        this.errorMessage = 'حصل خطأ أثناء إرسال الرد';
        this.isSending = false;
      },
    });
  }

  updateStatus(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const status = select.value as ChatStatus;

    if (!this.conversation || status === this.conversation.status) {
      return;
    }

    const oldStatus = this.conversation.status;
    this.isUpdating = true;
    this.errorMessage = '';

    this.chatService.adminUpdateStatus(this.conversationId, status).subscribe({
      next: (res) => {
        this.conversation = {
          ...this.conversation!,
          ...res.conversation,
        };
        this.isUpdating = false;
      },
      error: () => {
        if (this.conversation) {
          this.conversation = {
            ...this.conversation,
            status: oldStatus,
          };
        }

        this.errorMessage = 'حصل خطأ أثناء تحديث الحالة';
        this.isUpdating = false;
      },
    });
  }

  backToChats(): void {
    this.router.navigate(['/allchats']);
  }

  getMessageText(message: ChatApiMessage): string {
    if (message.message) {
      return message.message;
    }

    if (message.message_key) {
      return this.getMessageKeyLabel(message.message_key);
    }

    return 'رسالة نظام';
  }

  getMessageKeyLabel(key: string): string {
    const normalizedKey = key.toLowerCase();

    switch (normalizedKey) {
      case 'atw.chat.messages.reply':
        return '';
      case 'atw.chat.messages.welcome':
        return 'أهلا بك في الشحات إن';
      case 'atw.chat.messages.help':
        return 'اختر سؤالا سريعا أو اكتب رسالتك وسنساعدك.';
      case 'atw.chat.quickmessages.jobs':
        return 'أريد معرفة الوظائف المتاحة';
      case 'atw.chat.quickmessages.cv':
        return 'أحتاج مساعدة في السيرة الذاتية';
      case 'atw.chat.quickmessages.travel':
        return 'أريد معرفة خطوات السفر';
      default:
        return key;
    }
  }

  getUserDisplayName(): string {
    if (!this.conversation) return '-';

    if (this.conversation.name) return this.conversation.name;

    if (this.conversation.user?.first_name || this.conversation.user?.last_name) {
      return `${this.conversation.user?.first_name || ''} ${this.conversation.user?.last_name || ''}`.trim();
    }

    if (this.conversation.phone) return this.conversation.phone;
    if (this.conversation.email) return this.conversation.email;

    return 'زائر';
  }

  getUserTypeLabel(type?: string): string {
    switch (type) {
      case 'candidate':
        return 'مرشح';
      case 'company':
        return 'شركة';
      case 'admin':
        return 'أدمن';
      default:
        return 'زائر';
    }
  }

  getStatusLabel(status?: string): string {
    switch (status) {
      case 'open':
        return 'مفتوح';
      case 'pending':
        return 'قيد المتابعة';
      case 'closed':
        return 'مغلق';
      default:
        return '-';
    }
  }

  getSenderLabel(sender: string): string {
    switch (sender) {
      case 'user':
        return 'العميل';
      case 'admin':
        return 'الأدمن';
      case 'bot':
        return 'مساعد الشحات';
      case 'system':
        return 'النظام';
      default:
        return sender;
    }
  }

  formatDate(date: string | null): string {
    if (!date) return '-';

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return '-';
    }

    return parsedDate.toLocaleString('ar-EG', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  private applyConversationResponse(res: any, forceScroll: boolean): void {
    const nextConversation = res?.conversation;

    if (!nextConversation) {
      return;
    }

    const oldLastMessageId = this.getLastMessageId(this.messages);
    const nextMessages = this.filterVisibleMessages(nextConversation.messages || []);
    const nextLastMessageId = this.getLastMessageId(nextMessages);
    const hasNewMessages =
      oldLastMessageId !== nextLastMessageId || this.messages.length !== nextMessages.length;

    this.conversation = {
      ...nextConversation,
      messages: nextMessages,
    };

    this.messages = nextMessages;

    if (forceScroll || hasNewMessages) {
      setTimeout(() => this.scrollToBottom(), 50);
    }
  }

  private filterVisibleMessages(messages: ChatApiMessage[]): ChatApiMessage[] {
    return messages.filter((message) => !this.isAutoBotReply(message));
  }

  private isAutoBotReply(message: ChatApiMessage): boolean {
    const key = (message.message_key || '').toLowerCase();

    return key === 'atw.chat.messages.reply';
  }

  private getLastMessageId(messages: ChatApiMessage[]): number | string | null {
    if (!messages.length) {
      return null;
    }

    return messages[messages.length - 1]?.id ?? null;
  }

  private scrollToBottom(): void {
    const element = this.messagesBox?.nativeElement;

    if (!element) {
      return;
    }

    element.scrollTop = element.scrollHeight;
  }
}
